import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, errorText } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { AuthLayout } from "@/components/brand/AuthLayout";
import { Field, FormError } from "@/components/brand/Field";

export default function AdminLogin() {
  const { setAdmin } = useAdminAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("password");
  const [form, setForm] = useState({ email: "", password: "" });
  const [mfa, setMfa] = useState({ token: "", enrolled: true, secret: "", uri: "", code: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/auth/login", form);
      if (data.mfa_enrolled) {
        setMfa({ ...mfa, token: data.mfa_token, enrolled: true });
        setStep("code");
      } else {
        const s = await api.post("/admin/auth/mfa/setup", { mfa_token: data.mfa_token });
        setMfa({ token: data.mfa_token, enrolled: false, secret: s.data.secret, uri: s.data.otpauth_uri, code: "" });
        setStep("enrol");
      }
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/auth/mfa/verify", { mfa_token: mfa.token, code: mfa.code });
      setAdmin(data);
      nav("/admin", { replace: true });
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout admin title={step === "password" ? "Admin sign in" : step === "enrol" ? "Set up two-step sign in" : "Enter your code"}
      subtitle={step === "password" ? "Internal console. Every action here is logged." : step === "enrol" ? "Scan this with an authenticator app (Google Authenticator, 1Password, Authy). Required for every admin." : "Open your authenticator app and enter the 6-digit code."}
      footer={step === "password" ? <Link className="ts-link" to="/admin/forgot" data-testid="admin-forgot-link">Forgot your password?</Link> : <button className="ts-link" onClick={() => setStep("password")} data-testid="admin-back-button">Start over</button>}>
      <FormError message={error} testId="admin-login-error" />
      {step === "password" && (
        <form onSubmit={login} noValidate>
          <Field id="admin-email" label="Email">
            <input id="admin-email" data-testid="admin-email-input" className="ts-input" type="email" autoComplete="username" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field id="admin-password" label="Password">
            <input id="admin-password" data-testid="admin-password-input" className="ts-input" type="password" autoComplete="current-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <button data-testid="admin-login-submit" className="ts-btn ts-btn-primary w-full" disabled={busy}>{busy ? "Checking…" : "Continue"}</button>
        </form>
      )}
      {step !== "password" && (
        <form onSubmit={verify} noValidate>
          {step === "enrol" && (
            <div className="mb-5">
              <div className="flex justify-center p-4 rounded border mb-3" style={{ borderColor: "var(--line)" }}>
                <QRCodeSVG value={mfa.uri} size={168} data-testid="mfa-qr" />
              </div>
              <p className="text-xs" style={{ color: "var(--muted-text)" }}>Can't scan? Enter this key manually:</p>
              <code className="num text-xs block mt-1 p-2 rounded break-all" style={{ background: "var(--paper)" }} data-testid="mfa-secret">{mfa.secret}</code>
            </div>
          )}
          <Field id="mfa-code" label="6-digit code">
            <input id="mfa-code" data-testid="mfa-code-input" className="ts-input num tracking-[0.3em] text-center text-lg" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code" required value={mfa.code} onChange={(e) => setMfa({ ...mfa, code: e.target.value.replace(/\D/g, "") })} />
          </Field>
          <button data-testid="mfa-submit" className="ts-btn ts-btn-primary w-full" disabled={busy || mfa.code.length !== 6}>{busy ? "Verifying…" : step === "enrol" ? "Confirm and sign in" : "Sign in"}</button>
        </form>
      )}
    </AuthLayout>
  );
}
